import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { isNil, omitBy } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import {
  IAddCartQuoteVariable,
  ICartMeta,
  IQuoteCart,
  IQuoteCartAddServicePayload,
  IQuoteCartEdit,
  IQuoteCartSubmitPayload,
  IQuoteCreatedResponse,
  IRimType,
} from '~/purchase/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IQuoteCart, ICartMeta>,
      IErrorApiResponse
    >
  >;
  addItem?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    IAddCartQuoteVariable
  >;
  removeItem?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    string
  >;
  updateItemQuantity?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    { id: string; body: { quantity: number } }
  >;
  updatePrices?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    {
      items?: Array<{ id: string; price: number }>;
      services?: Array<{ id: string; price: number }>;
    }
  >;
  reset?: UseMutationOptions<ISuccessApiResponse, IErrorApiResponse>;
  updateRimType?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    { rimType: IRimType }
  >;
  addService?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    IQuoteCartAddServicePayload
  >;
  removeService?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    string
  >;
  submit?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCreatedResponse>,
    IErrorApiResponse,
    IQuoteCartSubmitPayload
  >;
  getFromQuote?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IQuoteCartEdit>, IErrorApiResponse>
  > & {
    args: {
      quoteReference: string;
    };
  };
  refreshMountingServices?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCart>,
    IErrorApiResponse,
    { body: IQuoteCart; reference: string }
  >;
};

export const useManageQuoteCart = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.quoteCart.base(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart, ICartMeta>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(idCustomer) && options.get?.enabled,
  });

  const addItemMutation = useMutation({
    mutationFn: (payload) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/items`,
            {
              body: JSON.stringify(payload),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.addItem,
  });

  const removeItemMutation = useMutation({
    mutationFn: (id) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/items/${id}`,
            {
              method: 'DELETE',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.removeItem,
  });

  const updateItemQuantityMutation = useMutation({
    mutationFn: ({ id, body }) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/items/${id}/quantity`,
            {
              body: JSON.stringify(body),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.updateItemQuantity,
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart`,
            {
              method: 'DELETE',
            },
          )
        : emptyQueryError(),

    ...options.reset,
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
  });

  const updateRimTypeMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/rim-type`,
            {
              body: JSON.stringify(variables),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.updateRimType,
  });

  const updatePricesMutation = useMutation({
    mutationFn: (body) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/prices`,
            {
              body: JSON.stringify(body),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.updatePrices,
  });

  const addServiceMutation = useMutation({
    mutationFn: (body) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/services`,
            {
              body: JSON.stringify(body),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.addService,
  });

  const removeServiceMutation = useMutation({
    mutationFn: (id) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/services/${id}`,
            {
              method: 'DELETE',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.removeService,
  });

  const submitMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCreatedResponse>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/submit`,
            {
              body: JSON.stringify(variables),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.quoteCart.base(idCustomer)],
    ...options.submit,
    mutationKey: PurchaseQueryKeys.quoteCart.submit(idCustomer),
  });

  const getFromQuote = useQuery({
    queryKey: PurchaseQueryKeys.getFromQuote.base(
      options.getFromQuote?.args.quoteReference as string,
    ),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCartEdit>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/quote/${options.getFromQuote?.args.quoteReference}`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    ...options.getFromQuote,
    enabled:
      Boolean(options.getFromQuote?.enabled) &&
      Boolean(options.getFromQuote?.args.quoteReference),
  });

  const refreshMountingServicesMutation = useMutation({
    mutationFn: ({ reference, body }) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IQuoteCart>>(
            `/offers/api/tenants/${tenantCode}/customers/${idCustomer}/quotes/cart/quote/${reference}/refresh-mounting-services`,
            {
              body: JSON.stringify(omitBy(body, isNil)),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    retry: false,
    ...options.refreshMountingServices,
  });

  return restrictApi(options, {
    get: getQuery,
    addItem: addItemMutation,
    removeItem: removeItemMutation,
    updateItemQuantity: updateItemQuantityMutation,
    updatePrices: updatePricesMutation,
    reset: resetMutation,
    updateRimType: updateRimTypeMutation,
    addService: addServiceMutation,
    removeService: removeServiceMutation,
    submit: submitMutation,
    getFromQuote: getFromQuote,
    refreshMountingServices: refreshMountingServicesMutation,
  });
};
